import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  BarChart3,
  Users,
  MousePointerClick,
  Clock,
  TrendingUp,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  ArrowLeft,
  RefreshCw,
  Calendar,
  MapPin,
  Link as LinkIcon,
  Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/analytics`;

const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
  <div className="card" style={{ padding: '1.5rem' }}>
    <div className="flex items-start justify-between">
      <div>
        <p className="body-small mb-1" style={{ color: 'var(--text-muted)' }}>{title}</p>
        <p className="heading-1" style={{ marginBottom: '0.25rem' }}>{value}</p>
        {subtitle && <p className="mono-text text-xs">{subtitle}</p>}
      </div>
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ background: color || 'var(--accent-blue-200)' }}
      >
        <Icon size={20} />
      </div>
    </div>
  </div>
);

const formatDuration = (seconds) => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.round(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
};

const formatTimestamp = (timestamp) => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'N/A';
  }
};

export const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/dashboard-stats?days=${days}`);
      setStats(response.data);
      setError(null);
    } catch (e) {
      setError('Failed to load analytics data');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchStats();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const getDeviceIcon = (device) => {
    switch (device?.toLowerCase()) {
      case 'mobile': return Smartphone;
      case 'tablet': return Tablet;
      default: return Monitor;
    }
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-page)' }}>
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto mb-4" size={32} />
          <p className="body-medium">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-page)' }}>
        <div className="text-center">
          <p className="body-medium mb-4">{error}</p>
          <Button onClick={fetchStats}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-page)' }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50 px-6 py-4"
        style={{
          background: 'var(--bg-overlay)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid var(--border-light)'
        }}
      >
        <div className="container flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center gap-2 hover:opacity-70">
              <ArrowLeft size={20} />
              <span className="body-small">Back to Site</span>
            </a>
            <div className="h-6 w-px" style={{ background: 'var(--border-light)' }} />
            <h1 className="heading-2">Analytics Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="px-3 py-2 rounded-lg body-small"
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-light)'
              }}
            >
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchStats}
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="container py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Visits"
            value={stats?.total_visits || 0}
            subtitle={`Last ${days} days`}
            icon={Eye}
            color="var(--accent-blue-200)"
          />
          <StatCard
            title="Unique Sessions"
            value={stats?.unique_sessions || 0}
            subtitle={`${stats?.bounce_rate || 0}% bounce rate`}
            icon={Users}
            color="var(--accent-purple-200)"
          />
          <StatCard
            title="Total Clicks"
            value={stats?.total_clicks || 0}
            subtitle="On tracked elements"
            icon={MousePointerClick}
            color="var(--accent-orange-200)"
          />
          <StatCard
            title="Avg. Time Spent"
            value={formatDuration(stats?.avg_time_spent_seconds || 0)}
            subtitle="Per page visit"
            icon={Clock}
            color="var(--accent-green-200)"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['overview', 'visits', 'sessions', 'clicks'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-full mono-text text-sm capitalize transition-all ${
                activeTab === tab ? 'btn-primary' : 'btn-secondary'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Visits Chart */}
            <div className="card" style={{ padding: '1.5rem' }}>
              <h3 className="heading-3 mb-4 flex items-center gap-2">
                <TrendingUp size={20} />
                Daily Visits
              </h3>
              <div className="space-y-2">
                {stats?.daily_visits?.slice(-7).map((day, index) => {
                  const maxVisits = Math.max(...(stats?.daily_visits?.map(d => d.visits) || [1]));
                  const percentage = (day.visits / maxVisits) * 100;
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <span className="mono-text text-xs w-20">{day.date.split('-').slice(1).join('/')}</span>
                      <div className="flex-1 h-6 rounded-full overflow-hidden" style={{ background: 'var(--bg-section)' }}>
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${percentage}%`,
                            background: 'var(--accent-blue-400)'
                          }}
                        />
                      </div>
                      <span className="mono-text text-sm w-10 text-right">{day.visits}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Device Breakdown */}
            <div className="card" style={{ padding: '1.5rem' }}>
              <h3 className="heading-3 mb-4 flex items-center gap-2">
                <Monitor size={20} />
                Device Breakdown
              </h3>
              <div className="space-y-3">
                {Object.entries(stats?.device_breakdown || {}).map(([device, count]) => {
                  const total = Object.values(stats?.device_breakdown || {}).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                  const DeviceIcon = getDeviceIcon(device);
                  return (
                    <div key={device} className="flex items-center gap-3">
                      <DeviceIcon size={18} />
                      <span className="body-small capitalize flex-1">{device}</span>
                      <span className="mono-text text-sm">{percentage}%</span>
                      <span className="mono-text text-xs" style={{ color: 'var(--text-muted)' }}>({count})</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top Pages */}
            <div className="card" style={{ padding: '1.5rem' }}>
              <h3 className="heading-3 mb-4 flex items-center gap-2">
                <LinkIcon size={20} />
                Top Pages
              </h3>
              <div className="space-y-2">
                {stats?.top_pages?.slice(0, 5).map((page, index) => (
                  <div key={index} className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <span className="body-small truncate flex-1 mr-4">{page.page}</span>
                    <span className="mono-text text-sm">{page.visits} visits</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Browser Breakdown */}
            <div className="card" style={{ padding: '1.5rem' }}>
              <h3 className="heading-3 mb-4 flex items-center gap-2">
                <Globe size={20} />
                Browser Breakdown
              </h3>
              <div className="space-y-3">
                {Object.entries(stats?.browser_breakdown || {}).slice(0, 5).map(([browser, count]) => {
                  const total = Object.values(stats?.browser_breakdown || {}).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                  return (
                    <div key={browser} className="flex items-center gap-3">
                      <span className="body-small flex-1">{browser}</span>
                      <span className="mono-text text-sm">{percentage}%</span>
                      <span className="mono-text text-xs" style={{ color: 'var(--text-muted)' }}>({count})</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* UTM Campaigns */}
            {stats?.utm_breakdown?.length > 0 && (
              <div className="card lg:col-span-2" style={{ padding: '1.5rem' }}>
                <h3 className="heading-3 mb-4 flex items-center gap-2">
                  <BarChart3 size={20} />
                  UTM Campaigns
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                        <th className="text-left py-2 body-small font-medium">Source / Medium / Campaign</th>
                        <th className="text-right py-2 body-small font-medium">Visits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.utm_breakdown.map((utm, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid var(--border-light)' }}>
                          <td className="py-2 mono-text text-sm">{utm.utm}</td>
                          <td className="py-2 mono-text text-sm text-right">{utm.visits}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Click Heatmap */}
            <div className="card lg:col-span-2" style={{ padding: '1.5rem' }}>
              <h3 className="heading-3 mb-4 flex items-center gap-2">
                <MousePointerClick size={20} />
                Most Clicked Elements
              </h3>
              <div className="flex flex-wrap gap-2">
                {stats?.click_heatmap?.map((item, index) => (
                  <div
                    key={index}
                    className="px-3 py-2 rounded-lg"
                    style={{
                      background: `rgba(188, 161, 130, ${0.2 + (item.clicks / (stats.click_heatmap[0]?.clicks || 1)) * 0.6})`,
                      border: '1px solid var(--border-light)'
                    }}
                  >
                    <span className="body-small">{item.element.substring(0, 30)}</span>
                    <span className="mono-text text-xs ml-2">({item.clicks})</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Recent Visits Tab */}
        {activeTab === 'visits' && (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ background: 'var(--bg-section)' }}>
                    <th className="text-left p-3 body-small font-medium">Timestamp</th>
                    <th className="text-left p-3 body-small font-medium">Page</th>
                    <th className="text-left p-3 body-small font-medium">IP Address</th>
                    <th className="text-left p-3 body-small font-medium">Device</th>
                    <th className="text-left p-3 body-small font-medium">Browser</th>
                    <th className="text-left p-3 body-small font-medium">Time Spent</th>
                    <th className="text-left p-3 body-small font-medium">UTM</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.recent_visits?.map((visit, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td className="p-3 mono-text text-xs">{formatTimestamp(visit.timestamp)}</td>
                      <td className="p-3 body-small truncate max-w-[200px]">{visit.page_url}</td>
                      <td className="p-3 mono-text text-xs">{visit.ip_address}</td>
                      <td className="p-3">
                        {(() => {
                          const DeviceIcon = getDeviceIcon(visit.device_type);
                          return <DeviceIcon size={16} />;
                        })()}
                      </td>
                      <td className="p-3 body-small">{visit.browser}</td>
                      <td className="p-3 mono-text text-xs">{formatDuration(visit.time_spent_seconds)}</td>
                      <td className="p-3 mono-text text-xs">
                        {visit.utm_source ? `${visit.utm_source}/${visit.utm_campaign || '-'}` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="space-y-4">
            {stats?.recent_sessions?.map((session, index) => (
              <div key={index} className="card" style={{ padding: '1.5rem' }}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="mono-text text-xs mb-1" style={{ color: 'var(--text-muted)' }}>
                      Session ID: {session.session_id.substring(0, 20)}...
                    </p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="flex items-center gap-1 body-small">
                        <MapPin size={14} /> {session.ip_address}
                      </span>
                      <span className="flex items-center gap-1 body-small">
                        {(() => {
                          const DeviceIcon = getDeviceIcon(session.device_type);
                          return <DeviceIcon size={14} />;
                        })()}
                        {session.device_type}
                      </span>
                      <span className="flex items-center gap-1 body-small">
                        <Globe size={14} /> {session.browser}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="mono-text text-xs">{formatTimestamp(session.first_visit)}</p>
                    <p className="body-small mt-1">
                      <Clock size={12} className="inline mr-1" />
                      {formatDuration(session.total_time)}
                    </p>
                  </div>
                </div>

                {/* Journey */}
                <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--border-light)' }}>
                  <p className="body-small mb-2 font-medium">Journey ({session.pages?.length || 0} pages)</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {session.pages?.map((page, pIndex) => (
                      <div key={pIndex} className="flex items-center gap-2">
                        <span
                          className="px-3 py-1 rounded-full mono-text text-xs"
                          style={{ background: 'var(--accent-blue-200)' }}
                        >
                          {page.url}
                        </span>
                        {pIndex < session.pages.length - 1 && (
                          <span style={{ color: 'var(--text-muted)' }}>→</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Clicks Tab */}
        {activeTab === 'clicks' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Hourly Distribution */}
            <div className="card" style={{ padding: '1.5rem' }}>
              <h3 className="heading-3 mb-4 flex items-center gap-2">
                <Calendar size={20} />
                Hourly Visit Distribution
              </h3>
              <div className="grid grid-cols-12 gap-1">
                {stats?.hourly_visits?.map((hour, index) => {
                  const maxVisits = Math.max(...(stats?.hourly_visits?.map(h => h.visits) || [1]));
                  const intensity = maxVisits > 0 ? hour.visits / maxVisits : 0;
                  return (
                    <div key={index} className="text-center">
                      <div
                        className="h-16 rounded-sm mb-1"
                        style={{
                          background: `rgba(118, 133, 151, ${0.1 + intensity * 0.8})`,
                        }}
                        title={`${hour.hour}:00 - ${hour.visits} visits`}
                      />
                      <span className="mono-text text-xs">{hour.hour}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Click Elements */}
            <div className="card" style={{ padding: '1.5rem' }}>
              <h3 className="heading-3 mb-4 flex items-center gap-2">
                <MousePointerClick size={20} />
                Click Distribution
              </h3>
              <div className="space-y-2">
                {stats?.click_heatmap?.slice(0, 10).map((item, index) => {
                  const maxClicks = stats.click_heatmap[0]?.clicks || 1;
                  const percentage = (item.clicks / maxClicks) * 100;
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <span className="body-small truncate flex-1">{item.element.substring(0, 25)}</span>
                      <div className="w-24 h-2 rounded-full overflow-hidden" style={{ background: 'var(--bg-section)' }}>
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${percentage}%`,
                            background: 'var(--accent-orange-400)'
                          }}
                        />
                      </div>
                      <span className="mono-text text-xs w-8 text-right">{item.clicks}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
